import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, ChevronRight, Loader2, AlertCircle, 
  FlaskConical, Bug, Leaf, Microscope, Tag, ArrowLeft, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Class configuration
const CLASS_CONFIG = {
  'Virus': { icon: FlaskConical, color: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200', bgColor: 'bg-red-50' },
  'Bacterium': { icon: Bug, color: 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200', bgColor: 'bg-blue-50' },
  'Fungus': { icon: Leaf, color: 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200', bgColor: 'bg-green-50' },
  'Parasite': { icon: Microscope, color: 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200', bgColor: 'bg-purple-50' },
  'Viroid': { icon: FlaskConical, color: 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200', bgColor: 'bg-orange-50' },
  'Others': { icon: FlaskConical, color: 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200', bgColor: 'bg-gray-50' },
  'Species Detection Assays': { icon: Tag, color: 'bg-yellow-100 text-yellow-700 border-yellow-300 hover:bg-yellow-200', bgColor: 'bg-yellow-50' },
};

// Tag colors
const TAG_COLORS = {
  'Human': 'bg-blue-50 text-blue-600',
  'Veterinary': 'bg-green-50 text-green-600',
  'Plant': 'bg-emerald-50 text-emerald-600',
  'Food': 'bg-orange-50 text-orange-600',
  'Water': 'bg-cyan-50 text-cyan-600',
  'Environmental': 'bg-amber-50 text-amber-600',
  'Industrial': 'bg-slate-50 text-slate-600',
  'Laboratory control': 'bg-pink-50 text-pink-600',
};

const ReadyAssays = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { addToCart } = useCart();

  // Get technology from location state or default to realtime
  const initialTechnology = location.state?.technology || 'realtime_pcr';
  const [activeTab, setActiveTab] = useState('pathogen_detection');
  const [technology] = useState(initialTechnology);

  // Get tag from URL if present
  const urlTag = searchParams.get('tag');

  // Pathogen state
  const [pathogens, setPathogens] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classesWithCounts, setClassesWithCounts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  // UI state - if URL has tag param, show "all" pathogens filtered by that tag
  const [selectedClass, setSelectedClass] = useState(urlTag ? 'all' : null);
  const [selectedTag, setSelectedTag] = useState(urlTag || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const limit = 50;

  // Fetch class distribution on mount
  useEffect(() => {
    fetchClassDistribution();
  }, []);

  // Handle URL tag parameter changes
  useEffect(() => {
    if (urlTag) {
      setSelectedClass('all');
      setSelectedTag(urlTag);
    }
  }, [urlTag]);

  // Fetch pathogens when class is selected or when filtering by tag
  useEffect(() => {
    if (selectedClass) {
      fetchPathogens();
    }
  }, [selectedClass, selectedTag, page]);

  const fetchClassDistribution = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pathogens/classes`);
      if (!response.ok) throw new Error('Failed to fetch classes');
      const data = await response.json();
      setClassesWithCounts(data.classes);
      setClasses(data.classes.map(c => c.name));
    } catch (err) {
      console.error('Error fetching classes:', err);
      setError(err.message);
    }
  };

  const fetchPathogens = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      // Only add class filter if it's not 'all'
      if (selectedClass && selectedClass !== 'all') {
        params.append('pathogen_class', selectedClass);
      }
      if (selectedTag) {
        params.append('related_tag', selectedTag);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const response = await fetch(`${API_URL}/api/pathogens?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch pathogens');

      const data = await response.json();
      setPathogens(data.pathogens);
      setTotal(data.total);
      setTags(data.tags);
    } catch (err) {
      console.error('Error fetching pathogens:', err);
      setError(err.message);
      toast.error('Failed to load pathogens');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    if (selectedClass) {
      fetchPathogens();
    }
  };

  const handleClassSelect = (className) => {
    setSelectedClass(className);
    setSelectedTag(null);
    setSearchQuery('');
    setPage(1);
    // Clear URL params when selecting a class
    setSearchParams({});
  };

  const handleBackToClasses = () => {
    setSelectedClass(null);
    setSelectedTag(null);
    setPathogens([]);
    setTotal(0);
    // Clear URL params
    setSearchParams({});
  };

  const totalPages = Math.ceil(total / limit);

  const technologyTitle = technology === 'realtime_pcr' ? 'Real-Time PCR' : 'Digital PCR';

  // Determine the page title based on context
  const getPageTitle = () => {
    if (selectedTag && selectedClass === 'all') {
      return `${selectedTag} Pathogens`;
    }
    return `${technologyTitle} Ready Assays`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-blue-200 text-sm mb-2">
            <button onClick={() => navigate('/')} className="hover:text-white">Home</button>
            <span>/</span>
            <button onClick={handleBackToClasses} className="hover:text-white">Pathogens</button>
            {selectedTag && selectedClass === 'all' && (
              <>
                <span>/</span>
                <span className="text-white">{selectedTag}</span>
              </>
            )}
            {selectedClass && selectedClass !== 'all' && (
              <>
                <span>/</span>
                <span className="text-white">{selectedClass}</span>
              </>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">{getPageTitle()}</h1>
          <p className="text-blue-100 text-lg max-w-3xl">
            {selectedTag && selectedClass === 'all' 
              ? `Browse all ${selectedTag.toLowerCase()} pathogen detection kits.`
              : 'Browse our catalog of pre-designed and validated detection kits. Each kit includes primers, probes, positive controls, and master mix.'
            }
          </p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-8 bg-white border shadow-sm">
            <TabsTrigger value="pathogen_detection" className="px-6">
              Pathogen Detection
            </TabsTrigger>
            {/* Future tabs can be added here */}
            {/* <TabsTrigger value="gene_expression">Gene Expression</TabsTrigger> */}
          </TabsList>

          <TabsContent value="pathogen_detection">
            {/* If no class selected, show class buttons */}
            {!selectedClass ? (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Select Pathogen Category</h2>
                  <p className="text-gray-600">Choose a category to browse available detection kits</p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {classesWithCounts.map((cls) => {
                    const config = CLASS_CONFIG[cls.name] || CLASS_CONFIG['Others'];
                    const IconComponent = config.icon;

                    return (
                      <button
                        key={cls.name}
                        onClick={() => handleClassSelect(cls.name)}
                        className={`p-6 rounded-xl border-2 ${config.color} transition-all text-left group cursor-pointer`}
                        data-testid={`class-btn-${cls.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-xl ${config.bgColor} flex items-center justify-center`}>
                            <IconComponent className="w-7 h-7" />
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold">{cls.name}</h3>
                            <p className="text-sm opacity-80">{cls.count} assays available</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-end mt-4 text-sm font-medium opacity-70 group-hover:opacity-100">
                          Browse A-Z list
                          <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Show pathogens list when class is selected */
              <div className="space-y-6">
                {/* Back button and class/tag header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <Button variant="outline" onClick={handleBackToClasses}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Categories
                  </Button>

                  <div className="flex items-center gap-3">
                    {selectedClass === 'all' && selectedTag ? (
                      <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-100 text-blue-700 border border-blue-300">
                        <Tag className="w-5 h-5" />
                        <span className="font-semibold">{selectedTag}</span>
                        <Badge variant="secondary">{total} assays</Badge>
                      </div>
                    ) : (
                      (() => {
                        const config = CLASS_CONFIG[selectedClass] || CLASS_CONFIG['Others'];
                        const IconComponent = config.icon;
                        return (
                          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${config.color}`}>
                            <IconComponent className="w-5 h-5" />
                            <span className="font-semibold">{selectedClass}</span>
                            <Badge variant="secondary">{total} assays</Badge>
                          </div>
                        );
                      })()
                    )}
                  </div>
                </div>

                {/* Search and filters */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <form onSubmit={handleSearch} className="flex-1">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <Input
                            placeholder={`Search ${selectedClass.toLowerCase()}...`}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                            data-testid="pathogen-search"
                          />
                        </div>
                      </form>

                      {/* Tag filter buttons */}
                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant={selectedTag === null ? "default" : "outline"}
                            size="sm"
                            onClick={() => { setSelectedTag(null); setPage(1); }}
                          >
                            All
                          </Button>
                          {tags.map(tag => (
                            <Button
                              key={tag}
                              variant={selectedTag === tag ? "default" : "outline"}
                              size="sm"
                              onClick={() => { setSelectedTag(tag); setPage(1); }}
                            >
                              {tag}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Loading State */}
                {loading && (
                  <div className="flex justify-center items-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600">Loading...</span>
                  </div>
                )}

                {/* Error State */}
                {error && !loading && (
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-gray-600">{error}</p>
                    <Button onClick={fetchPathogens} className="mt-4">Try Again</Button>
                  </div>
                )}

                {/* Pathogens A-Z List */}
                {!loading && !error && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pathogens.map(pathogen => (
                      <Card
                        key={pathogen.id}
                        className="hover:shadow-md transition-shadow cursor-pointer group"
                        onClick={() => navigate(`/pathogen/${pathogen.id}`)}
                        data-testid={`pathogen-card-${pathogen.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                              {pathogen.species}
                            </h3>
                            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                          </div>

                          {/* Related Tags */}
                          <div className="flex flex-wrap gap-1 mt-3">
                            {pathogen.related_tags.slice(0, 3).map(tag => (
                              <span
                                key={tag}
                                className={`text-xs px-2 py-0.5 rounded-full ${TAG_COLORS[tag] || 'bg-gray-50 text-gray-600'}`}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>

                          {/* Price Preview */}
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <span className="text-sm text-gray-500">Starting from </span>
                            <span className="font-semibold text-blue-600">€400</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Empty State */}
                {!loading && !error && pathogens.length === 0 && selectedClass && (
                  <div className="text-center py-12">
                    <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">No pathogens found</p>
                    <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-4 mt-8">
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ReadyAssays;
